import { Globe } from "lucide-react";
import { LANGS, useI18n, type Lang } from "@/lib/useI18n";
import { useEffect, useRef, useState } from "react";

export function LanguageSwitcher({ compact = false }: { compact?: boolean }) {
  const { lang, setLang, t } = useI18n();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const onDoc = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const current = LANGS.find((l) => l.code === lang) ?? LANGS[0];

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-label={t("lang.label")}
        aria-haspopup="listbox"
        aria-expanded={open}
        className="inline-flex items-center gap-1.5 rounded-full border border-border bg-card/70 px-3 py-2 text-xs font-semibold text-foreground/80 shadow-soft transition-all hover:border-accent/60 hover:text-foreground"
      >
        <Globe className="h-3.5 w-3.5" />
        <span className="uppercase tracking-wider">{current.code}</span>
        {!compact && <span className="hidden sm:inline text-foreground/60">· {current.native}</span>}
      </button>
      {open && (
        <ul
          role="listbox"
          className="absolute right-0 z-50 mt-2 w-44 origin-top-right overflow-hidden rounded-xl border border-border bg-popover py-1.5 shadow-elegant"
        >
          {LANGS.map((l) => {
            const active = l.code === lang;
            return (
              <li key={l.code}>
                <button
                  type="button"
                  role="option"
                  aria-selected={active}
                  onClick={() => {
                    setLang(l.code as Lang);
                    setOpen(false);
                  }}
                  className={`flex w-full items-center justify-between gap-3 px-3.5 py-2 text-sm transition-colors ${
                    active
                      ? "bg-accent/15 text-foreground font-semibold"
                      : "text-foreground/80 hover:bg-secondary"
                  }`}
                >
                  <span>{l.native}</span>
                  <span className="text-[10px] uppercase tracking-widest text-muted-foreground">{l.code}</span>
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
